import { useEffect, useRef, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import * as Cesium from 'cesium'
import { applyVWorldBasemap } from '../VWorldBasemapSelector'
import { applyPointSize, applyVoxelOpacity, authToken } from './cdVoxelViz'

interface Props {
  /** 대시보드 공유 뷰어 (좌측 = 기준 시점 A) */
  viewerRef: RefObject<Cesium.Viewer | null>
  /** 대시보드 뷰어의 컨테이너 — 이 안에서 좌/우 분할한다 */
  container: HTMLElement
  /** 우측(대상 시점 B)에 올릴 3D Tiles URL */
  targets: { kind: 'original' | 'voxel'; url: string }[]
  voxelOpacity: number
  /** 원본이 포인트 클라우드일 때만 점 크기를 적용한다 */
  pointCloud: boolean
  pointSize: number
  labelA: string
  labelB: string
}

function tilesetResource(url: string): Cesium.Resource {
  const proxied = url.startsWith('/')
  const token = authToken()
  return new Cesium.Resource({
    url: proxied ? `${window.location.origin}${url}` : url,
    headers: proxied && token ? { Authorization: `Bearer ${token}` } : {},
  })
}

function copyCamera(from: Cesium.Viewer, to: Cesium.Viewer) {
  const c = from.camera
  to.camera.setView({
    destination: Cesium.Cartesian3.clone(c.positionWC),
    orientation: {
      direction: Cesium.Cartesian3.clone(c.directionWC),
      up: Cesium.Cartesian3.clone(c.upWC),
    },
  })
}

/**
 * Split View (Geo-link) — 대시보드 뷰어를 좌측 50% 로 줄이고, 우측 50% 에 두 번째 뷰어를 붙여
 * 두 시점(A | B)을 나란히 보여준다. 마우스가 올라간 쪽 뷰어를 기준으로 카메라를 서로 동기화한다.
 * 좌측(A)의 타일은 useCdMapLayers 가 대시보드 뷰어에 올리고, 우측(B)은 여기서 올린다.
 */
export default function CdSplitView({
  viewerRef,
  container,
  targets,
  voxelOpacity,
  pointCloud,
  pointSize,
  labelA,
  labelB,
}: Props) {
  const rightViewerRef = useRef<Cesium.Viewer | null>(null)
  const tilesetsRef = useRef<Map<'original' | 'voxel', Cesium.Cesium3DTileset>>(new Map())
  const latest = useRef({ voxelOpacity, pointCloud, pointSize })
  latest.current = { voxelOpacity, pointCloud, pointSize }
  const targetsKey = targets.map((t) => `${t.kind}|${t.url}`).join(',')

  // 레이아웃 + 우측 뷰어 + 카메라 동기화
  useEffect(() => {
    const main = viewerRef.current
    if (!main || main.isDestroyed()) return
    const mainEl = container.querySelector<HTMLElement>(':scope > .cesium-viewer')

    const host = document.createElement('div')
    host.style.cssText =
      'position:absolute;top:0;right:0;bottom:0;width:50%;border-left:2px solid #fff;z-index:2;'
    container.appendChild(host)
    if (mainEl) mainEl.style.width = '50%'

    const right = new Cesium.Viewer(host, {
      baseLayerPicker: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      navigationHelpButton: false,
      sceneModePicker: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      selectionIndicator: false,
      infoBox: false,
    })
    rightViewerRef.current = right
    applyVWorldBasemap(right, 'Base')

    let alive = true
    const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api'
    const token = authToken()
    Cesium.CesiumTerrainProvider.fromUrl(
      new Cesium.Resource({
        url: `${apiUrl}/terrain`,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
    )
      .then((tp) => {
        if (alive && !right.isDestroyed()) right.terrainProvider = tp
      })
      .catch((e: unknown) => console.error('Split View Terrain 로드 실패:', e))

    main.resize()
    copyCamera(main, right)

    // 마우스가 올라간 쪽이 leader — 매 프레임 leader 카메라를 follower 에 복사한다.
    let leader: 'left' | 'right' = 'left'
    const onLeft = () => (leader = 'left')
    const onRight = () => (leader = 'right')
    main.canvas.addEventListener('pointerenter', onLeft)
    main.canvas.addEventListener('wheel', onLeft)
    right.canvas.addEventListener('pointerenter', onRight)
    right.canvas.addEventListener('wheel', onRight)

    const syncLeft = () => {
      if (leader === 'left' && !right.isDestroyed()) copyCamera(main, right)
    }
    const syncRight = () => {
      if (leader === 'right' && !main.isDestroyed()) copyCamera(right, main)
    }
    const offLeft = main.scene.preRender.addEventListener(syncLeft)
    const offRight = right.scene.preRender.addEventListener(syncRight)

    return () => {
      alive = false
      offLeft()
      offRight()
      if (!main.isDestroyed()) {
        main.canvas.removeEventListener('pointerenter', onLeft)
        main.canvas.removeEventListener('wheel', onLeft)
        if (mainEl) mainEl.style.width = ''
        main.resize()
      }
      tilesetsRef.current.clear()
      rightViewerRef.current = null
      if (!right.isDestroyed()) right.destroy()
      host.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container])

  // 우측 뷰어의 대상 타일셋
  useEffect(() => {
    const right = rightViewerRef.current
    if (!right || right.isDestroyed()) return
    let cancelled = false

    tilesetsRef.current.forEach((ts) => !ts.isDestroyed() && right.scene.primitives.remove(ts))
    tilesetsRef.current.clear()

    targets.forEach(({ kind, url }) => {
      Cesium.Cesium3DTileset.fromUrl(tilesetResource(url))
        .then((ts) => {
          if (cancelled || right.isDestroyed()) {
            if (!ts.isDestroyed()) ts.destroy()
            return
          }
          right.scene.primitives.add(ts)
          tilesetsRef.current.set(kind, ts)
          const l = latest.current
          if (kind === 'voxel') applyVoxelOpacity(ts, l.voxelOpacity)
          else if (l.pointCloud) applyPointSize(ts, l.pointSize)
        })
        .catch((e: unknown) => console.error('Split View 대상 타일 로드 실패', e))
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey])

  useEffect(() => {
    const ts = tilesetsRef.current.get('voxel')
    if (ts) applyVoxelOpacity(ts, voxelOpacity)
  }, [voxelOpacity])

  useEffect(() => {
    const ts = tilesetsRef.current.get('original')
    if (ts && pointCloud) applyPointSize(ts, pointSize)
  }, [pointCloud, pointSize])

  // 라벨은 지도 컨테이너 안에 그려야 지도 화면 기준(A=좌상단, B=우상단)으로 배치된다.
  return createPortal(
    <div className="pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-between px-3">
      <span className="rounded bg-slate-900/80 px-2 py-0.5 text-[11px] font-semibold text-white">A · {labelA}</span>
      <span className="rounded bg-slate-900/80 px-2 py-0.5 text-[11px] font-semibold text-white">B · {labelB}</span>
    </div>,
    container,
  )
}
